import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Container from "react-bootstrap/Container";
import ReactQuill from 'react-quill';
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import CategoryList from "../CategoryList";
import 'react-quill/dist/quill.snow.css';
import CatalogService from "../../services/catalog.service";
import LogoService from "../../services/logo.service";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { getAdminCatalogItem, selectCatalogItem, getIgniteProducts, selectQuantityPrices } from '../../reducers/catalog';
import { getSubsite } from '../../reducers/auth';
import { useSelector } from 'react-redux';
import { Row, Col, Card, Badge } from "react-bootstrap";
import { XCircle, PlusCircle, GripVertical } from 'react-bootstrap-icons';
import { resolveImageUrl } from '../../utils/imageUrl';


const PRICING_TYPES = {
    FLAT: 1,
    QUANTITY: 2,
    BILL_LATER: 3
};

const BACKEND_URL = process.env.REACT_APP_LOCALHOST_URL;

const AdminAddProduct = () => {
    // title, description, htmlDescription, maker, source, brandLogo, styleImage, styleThumb, specSheet, measurements
    const { register, setValue, handleSubmit, watch, formState: { errors } } = useForm();
    const [categoryError, setCategoryError] = useState(false);
    const [editedHtmlDescription, setEditedHtmlDescription] = useState('');
    const [showDescription, setShowDescription] = useState(false);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [colorsData, setColorsData] = useState([
        { colorName: '', hexColor: '#000000', mainImageFile: null, mainImagePreview: null, additionalImageFiles: [], additionalImagePreviews: [] }
    ]);
    const [sizes, setSizes] = useState([]);
    const [sizeInput, setSizeInput] = useState('');
    const dispatch = useDispatch();
    const itemStatus = useSelector(state => state.catalog.adminItemStatus);
    const catalogItem = useSelector(selectCatalogItem);
    const style = useSelector(state => state.catalog.style);
    const storedQuantityPrices = useSelector(selectQuantityPrices);
    const currentSubsite = useSelector(getSubsite);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [productId, setProductId] = useState(null);
    const navigate = useNavigate();
    const [pricingType, setPricingType] = useState(PRICING_TYPES.FLAT);
    const [quantityPrices, setQuantityPrices] = useState([
        { quantity: 10, price: '' },
        { quantity: 50, price: '' },
        { quantity: 100, price: '' }
    ]);
    const [productType, setProductType] = useState('');
    const [personalizationFields, setPersonalizationFields] = useState([]);
    const [showPersonalizationForm, setShowPersonalizationForm] = useState(false);
    const [decoColors, setDecoColors] = useState([]);

    useEffect(() => {
        const loadDecoColors = async () => {
            try {
                const res = await LogoService.getLogoColors();
                setDecoColors(res?.data || []);
            } catch (error) {
                console.error("Error loading decoration colors:", error);
            }
        };
        loadDecoColors();
    }, []);

    useEffect(() => {
        const pid = searchParams.get("productId");
        console.log("PRODUCT ID", pid);
        if (pid) {
            dispatch(getAdminCatalogItem(pid));
            setProductId(pid);
            setValue('productId', pid);
        }
    }, [dispatch, searchParams, setValue]);

    useEffect(() => {
        console.log("IN EFFECT", itemStatus);
        if (itemStatus === 'loading') {
            setLoading(true);
        } else if (itemStatus === 'succeeded') {
            console.log("ITEM", catalogItem);
            setValue('title', catalogItem[0].title);
            setEditedHtmlDescription(catalogItem[0].description);
            setValue('modelCode', catalogItem[0].code);
            setValue('maker', catalogItem[0].maker);
            setValue('price', catalogItem[0].sizes?.[0]?.costSingle2X);
            // Populate colorsData from catalog item (each element is a color)
            const editColors = catalogItem.map(colorItem => ({
                colorId: colorItem.colorId || null,
                colorName: colorItem.colorName || '',
                hexColor: colorItem.hexColor || '#000000',
                mainImageFile: null,
                mainImagePreview: resolveImageUrl(colorItem.frontModelImage) || null,
                additionalImageFiles: [],
                additionalImagePreviews: []
            }));
            setColorsData(editColors.length > 0 ? editColors : [{ colorId: null, colorName: '', hexColor: '#000000', mainImageFile: null, mainImagePreview: null, additionalImageFiles: [], additionalImagePreviews: [] }]);
            // Populate shared sizes from the first color's sizes
            if (catalogItem[0].sizes) {
                setSizes(Object.values(catalogItem[0].sizes).map(s => s.sizeName));
            }
            // Also set productId from the loaded item
            if (catalogItem[0].productId) {
                setValue('productId', catalogItem[0].productId);
                setProductId(catalogItem[0].productId);
            }
            // Set categories from the style's ignite_categories
            if (style && style.ignite_categories && style.ignite_categories.length > 0) {
                const categoryIds = style.ignite_categories.map(cat => cat.id);
                setSelectedCategories(categoryIds);
            }

            // Set pricing type if available
            if (catalogItem[0].pricingType) {
                setPricingType(catalogItem[0].pricingType);
            }
            // Set quantity prices from Redux state (not from catalogItem)
            if (storedQuantityPrices && storedQuantityPrices.length > 0) {
                setQuantityPrices(storedQuantityPrices);
            }
            // Set product type if available
            if (style && style.productType) {
                setProductType(style.productType);
            }

            // Load personalization fields if style exists
            if (style && style.id && currentSubsite && currentSubsite.id) {
                loadPersonalizationFields(style.id, currentSubsite.id);
            }

            setLoading(false);
        }
    }, [itemStatus, dispatch, setValue, catalogItem, style, storedQuantityPrices, currentSubsite]);

    const loadPersonalizationFields = async (styleId, subsiteId) => {
        try {
            const fields = await CatalogService.getPersonalizationFields(styleId, subsiteId);
            const processedFields = fields.map(f => ({
                ...f,
                options: f.options ? JSON.parse(f.options) : [],
                colorMappings: f.colorMappings || []
            }));
            setPersonalizationFields(processedFields);
        } catch (error) {
            console.error("Error loading personalization fields:", error);
        }
    };

    const onSubmit = async (data) => {
        if (selectedCategories.length === 0) {
            setCategoryError(true);
            return;
        }
        const validColors = colorsData.filter(c => c.colorName.trim());
        if (validColors.length === 0) {
            alert("Please add at least one color.");
            return;
        }
        if (sizes.length === 0) {
            alert("Please add at least one size.");
            return;
        }
        setCategoryError(false);

        data.categories = [...selectedCategories];
        data.description = editedHtmlDescription;
        data.pricingType = pricingType;
        data.quantityPrices = pricingType === PRICING_TYPES.QUANTITY ? quantityPrices : [];
        data.productType = productType;
        // Attach the shared sizes to every color before sending
        data.colorsData = validColors.map(c => ({ ...c, sizes }));

        const response = await CatalogService.addProduct(data);
        console.log(response);
        if (response) {
            dispatch(getIgniteProducts());
            navigate('/admin/igniteproducts');
        }
    }

    const updateQuantityPrice = (index, field, value) => {
        const newPrices = [...quantityPrices];
        newPrices[index][field] = field === 'price' ? value : parseInt(value) || '';
        setQuantityPrices(newPrices);
    };

    const addQuantityTier = () => {
        const lastTier = quantityPrices[quantityPrices.length - 1];
        const newQty = lastTier.quantity ? lastTier.quantity + 50 : 50;
        setQuantityPrices([...quantityPrices, { quantity: newQty, price: '' }]);
    };

    const removeQuantityTier = (index) => {
        if (quantityPrices.length > 1) {
            setQuantityPrices(quantityPrices.filter((_, i) => i !== index));
        }
    };

    // Shared size handlers
    const addSize = () => {
        const sizesToAdd = sizeInput.split(',').map(s => s.trim()).filter(s => s);
        if (sizesToAdd.length === 0) return;
        setSizes([...sizes, ...sizesToAdd]);
        setSizeInput('');
    };

    const removeSize = (index) => {
        setSizes(sizes.filter((_, i) => i !== index));
    };

    // Color management handlers
    const addColor = () => {
        setColorsData([...colorsData, { colorName: '', hexColor: '#000000', mainImageFile: null, mainImagePreview: null, additionalImageFiles: [], additionalImagePreviews: [] }]);
    };

    const removeColor = (colorIndex) => {
        setColorsData(colorsData.filter((_, i) => i !== colorIndex));
    };

    const updateColorField = (colorIndex, field, value) => {
        const updated = [...colorsData];
        updated[colorIndex] = { ...updated[colorIndex], [field]: value };
        setColorsData(updated);
    };

    const handleColorMainImage = (colorIndex, event) => {
        const f = event.target.files[0];
        if (!f) return;
        const preview = URL.createObjectURL(f);
        const updated = [...colorsData];
        updated[colorIndex] = { ...updated[colorIndex], mainImageFile: f, mainImagePreview: preview };
        setColorsData(updated);
    };

    const handleColorAdditionalImages = (colorIndex, event) => {
        const newFiles = Array.from(event.target.files);
        const newPreviews = newFiles.map(f => URL.createObjectURL(f));
        const updated = [...colorsData];
        updated[colorIndex] = {
            ...updated[colorIndex],
            additionalImageFiles: [...updated[colorIndex].additionalImageFiles, ...newFiles],
            additionalImagePreviews: [...updated[colorIndex].additionalImagePreviews, ...newPreviews]
        };
        setColorsData(updated);
    };

    const removeColorAdditionalImage = (colorIndex, imageIndex) => {
        const updated = [...colorsData];
        URL.revokeObjectURL(updated[colorIndex].additionalImagePreviews[imageIndex]);
        updated[colorIndex] = {
            ...updated[colorIndex],
            additionalImageFiles: updated[colorIndex].additionalImageFiles.filter((_, i) => i !== imageIndex),
            additionalImagePreviews: updated[colorIndex].additionalImagePreviews.filter((_, i) => i !== imageIndex)
        };
        setColorsData(updated);
    };

    const updateIgniteCategoryForStyle = async (categoryId, checked) => {
        let newCategories;
        if (checked) {
            newCategories = [...selectedCategories, categoryId];
        } else {
            newCategories = [...selectedCategories];
            const index = newCategories.indexOf(categoryId);
            if (index > -1) {
                newCategories.splice(index, 1);
            }
        }
        setSelectedCategories(newCategories);
        // Clear category error when a category is selected
        if (newCategories.length > 0) {
            setCategoryError(false);
        }
    }

    const validateDescription = () => {
        return editedHtmlDescription != null && editedHtmlDescription != "";
    }

    // Personalization field management functions
    const addPersonalizationField = () => {
        setPersonalizationFields([
            ...personalizationFields,
            { name: '', fieldType: 'text', options: [], required: false, maxLength: 100, placeholder: '', price: '', decoColorId: '', colorMappings: [] }
        ]);
        setShowPersonalizationForm(true);
    };

    const updatePersonalizationField = (index, field, value) => {
        const updated = [...personalizationFields];
        updated[index][field] = value;
        setPersonalizationFields(updated);
    };

    const removePersonalizationField = async (index) => {
        const field = personalizationFields[index];
        if (field.id && currentSubsite && currentSubsite.id) {
            try {
                await CatalogService.deletePersonalizationField(field.id, currentSubsite.id);
            } catch (error) {
                console.error("Error deleting field:", error);
            }
        }
        setPersonalizationFields(personalizationFields.filter((_, i) => i !== index));
    };

    const addSelectOption = (fieldIndex) => {
        const updated = [...personalizationFields];
        updated[fieldIndex].options = [...(updated[fieldIndex].options || []), ''];
        setPersonalizationFields(updated);
    };

    const updateSelectOption = (fieldIndex, optionIndex, value) => {
        const updated = [...personalizationFields];
        updated[fieldIndex].options[optionIndex] = value;
        setPersonalizationFields(updated);
    };

    const updateColorMapping = (fieldIndex, colorId, decoColorId) => {
        const updated = [...personalizationFields];
        const maps = [...(updated[fieldIndex].colorMappings || [])];
        const idx = maps.findIndex(m => String(m.colorId) === String(colorId));
        if (!decoColorId) {
            if (idx >= 0) maps.splice(idx, 1);
        } else if (idx >= 0) {
            maps[idx] = { ...maps[idx], colorId, decoColorId };
        } else {
            maps.push({ colorId, decoColorId });
        }
        updated[fieldIndex].colorMappings = maps;
        setPersonalizationFields(updated);
    };

    const getMappedDecoColorId = (field, colorId) => {
        const map = (field.colorMappings || []).find(m => String(m.colorId) === String(colorId));
        return map?.decoColorId || '';
    };

    const removeSelectOption = (fieldIndex, optionIndex) => {
        const updated = [...personalizationFields];
        updated[fieldIndex].options = updated[fieldIndex].options.filter((_, i) => i !== optionIndex);
        setPersonalizationFields(updated);
    };

    const savePersonalizationFields = async () => {
        if (!style || !style.id) {
            alert("Please save the product first before adding personalization fields.");
            return;
        }
        if (!currentSubsite || !currentSubsite.id) {
            alert("No subsite selected.");
            return;
        }
        try {
            await CatalogService.savePersonalizationFields(style.id, personalizationFields, currentSubsite.id);
            alert("Personalization fields saved successfully for " + currentSubsite.subsiteName + "!");
        } catch (error) {
            console.error("Error saving personalization fields:", error);
            alert("Error saving personalization fields");
        }
    };

    return (
        <Container>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <input
                    type="hidden"
                    id="description"
                    {...register('description', {
                        validate: validateDescription,
                    })}
                />
                <input
                    type="hidden"
                    id="productId"
                    value={productId}
                    {...register('productId')}
                />


                <div class="product-details-container">
                    <div class="product-content">
                        <div style={{ borderBottom: "1px solid #000" }}>
                            <Form.Group className="mb-3" controlId="title">
                                <Form.Label className="sectionTitle">Title</Form.Label>
                                <input {...register("title", { required: true })} /><br />
                                {errors.title && <div className="error" >Title is required</div>}
                            </Form.Group>
                        </div>
                        <div style={{ borderBottom: "1px solid #000" }}>
                            <Form.Group className="mb-3" controlId="modelCode">
                                <Form.Label className="sectionTitle">Model Code</Form.Label>
                                <input {...register("modelCode", { required: true })} /><br />
                                {errors.modelCode && <div className="error" >Model Code is required</div>}
                            </Form.Group>
                        </div>
                        <div style={{ borderBottom: "1px solid #000" }}>
                            <Form.Group className="mb-3" controlId="productType">
                                <Form.Label className="sectionTitle">Product Type</Form.Label>
                                <Form.Select
                                    value={productType}
                                    onChange={(e) => setProductType(e.target.value)}
                                    style={{ maxWidth: 250 }}
                                >
                                    <option value="">Select Type...</option>
                                    <option value="Accessory">Accessory</option>
                                    <option value="Print">Print</option>
                                </Form.Select>
                            </Form.Group>
                        </div>
                        <div style={{ paddingBottom: 15, borderBottom: "1px solid #000" }}>
                            <Form.Label className="sectionTitle">Description</Form.Label>

                            <ReactQuill theme="snow" value={editedHtmlDescription} onChange={setEditedHtmlDescription} />
                            {errors.description && (
                                <div className="error" >Description is required</div>
                            )}
                        </div>
                        <div style={{ borderBottom: "1px solid #000", paddingBottom: 15 }}>
                            <Form.Group className="mb-3" controlId="pricingType">
                                <Form.Label className="sectionTitle">Pricing Type</Form.Label>
                                <Form.Select
                                    value={pricingType}
                                    onChange={(e) => setPricingType(parseInt(e.target.value))}
                                    style={{ maxWidth: 250 }}
                                >
                                    <option value={PRICING_TYPES.FLAT}>Flat Price</option>
                                    <option value={PRICING_TYPES.QUANTITY}>Quantity Pricing</option>
                                    <option value={PRICING_TYPES.BILL_LATER}>Bill Later</option>
                                </Form.Select>
                            </Form.Group>

                            {pricingType === PRICING_TYPES.FLAT && (
                                <Form.Group className="mb-3" controlId="price">
                                    <Form.Label className="sectionTitle">Price</Form.Label>
                                    <input {...register("price", { required: pricingType === PRICING_TYPES.FLAT })} /><br />
                                    {errors.price && <div className="error">Price is required</div>}
                                </Form.Group>
                            )}

                            {pricingType === PRICING_TYPES.QUANTITY && (
                                <div>
                                    <Form.Label className="sectionTitle">Quantity Pricing Tiers</Form.Label>
                                    {quantityPrices.map((tier, index) => (
                                        <Row key={index} className="mb-2 align-items-center">
                                            <Col xs={4}>
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Quantity"
                                                    value={tier.quantity}
                                                    onChange={(e) => updateQuantityPrice(index, 'quantity', e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={4}>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    placeholder="Price"
                                                    value={tier.price}
                                                    onChange={(e) => updateQuantityPrice(index, 'price', e.target.value)}
                                                />
                                            </Col>
                                            <Col xs={4}>
                                                <Button variant="outline-danger" size="sm" onClick={() => removeQuantityTier(index)}>
                                                    <XCircle />
                                                </Button>
                                            </Col>
                                        </Row>
                                    ))}
                                    <Button variant="outline-primary" size="sm" onClick={addQuantityTier}>
                                        + Add Tier
                                    </Button>
                                </div>
                            )}

                            {pricingType === PRICING_TYPES.BILL_LATER && (
                                <div className="text-muted">
                                    <small>Price will be determined and billed separately after ordering.</small>
                                </div>
                            )}
                        </div>

                        <div style={{ borderBottom: "1px solid #000" }}>
                            <CategoryList editEnabled={true} updateIgniteCategoryForStyle={updateIgniteCategoryForStyle} />
                            {categoryError && (
                                <div className="error" >Select at least 1 category</div>
                            )}

                        </div>
                        {/* Shared Sizes Section */}
                        <div style={{ borderBottom: "1px solid #000", paddingBottom: 15, marginTop: 10 }}>
                            <Form.Label className="sectionTitle">Sizes</Form.Label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                                {sizes.map((sz, sizeIndex) => (
                                    <Badge key={sizeIndex} bg="secondary" style={{ fontSize: 13, padding: '5px 8px', display: 'inline-flex', alignItems: 'center' }}>
                                        {sz}
                                        <XCircle style={{ marginLeft: 5, cursor: 'pointer' }} onClick={() => removeSize(sizeIndex)} />
                                    </Badge>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: 5 }}>
                                <Form.Control
                                    type="text"
                                    placeholder="S, M, L or one at a time"
                                    value={sizeInput}
                                    onChange={(e) => setSizeInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSize(); } }}
                                    style={{ maxWidth: 220 }}
                                />
                                <Button variant="outline-secondary" size="sm" onClick={addSize}>Add</Button>
                            </div>
                            <small style={{ color: '#666' }}>Separate multiple sizes with commas, or add one at a time. These sizes apply to all colors.</small>
                        </div>

                        {/* Colors Section */}
                        <div style={{ borderBottom: "1px solid #000", paddingBottom: 15, marginTop: 10 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <Form.Label className="sectionTitle" style={{ marginBottom: 0 }}>Colors</Form.Label>
                                <Button variant="outline-primary" size="sm" onClick={addColor}>
                                    <PlusCircle style={{ marginRight: 5 }} /> Add Color
                                </Button>
                            </div>

                            {colorsData.map((color, colorIndex) => (
                                <Card key={colorIndex} style={{ marginBottom: 12, padding: 12, border: '1px solid #dee2e6' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            {/* Color name + swatch */}
                                            <Row className="mb-2">
                                                <Col xs={8}>
                                                    <Form.Label>Color Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g., Navy Blue"
                                                        value={color.colorName}
                                                        onChange={(e) => updateColorField(colorIndex, 'colorName', e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={4}>
                                                    <Form.Label>Swatch</Form.Label>
                                                    <Form.Control
                                                        type="color"
                                                        value={color.hexColor}
                                                        onChange={(e) => updateColorField(colorIndex, 'hexColor', e.target.value)}
                                                        style={{ height: 38, padding: 2 }}
                                                    />
                                                </Col>
                                            </Row>

                                            {/* Main Image */}
                                            <div className="mb-2">
                                                <Form.Label>Main Image</Form.Label>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    <input type="file" accept="image/*" onChange={(e) => handleColorMainImage(colorIndex, e)} />
                                                    {color.mainImagePreview && (
                                                        <img src={color.mainImagePreview} alt="main" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Additional Images */}
                                            <div>
                                                <Form.Label>Additional Images</Form.Label>
                                                <input type="file" accept="image/*" multiple onChange={(e) => handleColorAdditionalImages(colorIndex, e)} />
                                                {color.additionalImagePreviews.length > 0 && (
                                                    <Row className="mt-2">
                                                        {color.additionalImagePreviews.map((preview, imgIndex) => (
                                                            <Col xs={3} key={imgIndex} style={{ position: 'relative', marginBottom: 8 }}>
                                                                <img src={preview} alt={`extra ${imgIndex + 1}`} style={{ width: '100%', height: 60, objectFit: 'cover' }} />
                                                                <XCircle
                                                                    onClick={() => removeColorAdditionalImage(colorIndex, imgIndex)}
                                                                    style={{ position: 'absolute', top: 2, right: 16, cursor: 'pointer', color: 'red', backgroundColor: 'white', borderRadius: '50%' }}
                                                                />
                                                            </Col>
                                                        ))}
                                                    </Row>
                                                )}
                                            </div>
                                        </div>

                                        {colorsData.length > 1 && (
                                            <Button variant="outline-danger" size="sm" onClick={() => removeColor(colorIndex)} style={{ marginLeft: 10, flexShrink: 0 }}>
                                                <XCircle />
                                            </Button>
                                        )}
                                    </div>
                                </Card>
                            ))}
                        </div>
                        <div style={{ borderBottom: "1px solid #000" }}>
                            <Form.Group className="mb-3" controlId="maker">
                                <Form.Label className="sectionTitle">Maker</Form.Label>
                                <input {...register("maker", { required: true })} /><br />
                                {errors.maker && <div className="error" >Maker is required</div>}
                            </Form.Group>
                        </div>

                        {/* Personalization Fields Section */}
                        <div style={{ borderBottom: "1px solid #000", paddingBottom: 15, marginTop: 15 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                <Form.Label className="sectionTitle" style={{ marginBottom: 0 }}>
                                    Personalization Fields
                                    {currentSubsite && (
                                        <Badge bg="secondary" style={{ marginLeft: 8, fontWeight: 'normal' }}>
                                            {currentSubsite.subsiteName}
                                        </Badge>
                                    )}
                                    {personalizationFields.length > 0 && (
                                        <Badge bg="info" style={{ marginLeft: 8 }}>{personalizationFields.length}</Badge>
                                    )}
                                </Form.Label>
                                <Button variant="outline-primary" size="sm" onClick={addPersonalizationField}>
                                    <PlusCircle style={{ marginRight: 5 }} /> Add Field
                                </Button>
                            </div>
                            <small style={{ color: '#666', display: 'block', marginBottom: 10 }}>
                                Add text fields or dropdowns for <strong>{currentSubsite?.subsiteName || 'this subsite'}</strong>. Each subsite can have different personalization fields.
                            </small>

                            {personalizationFields.map((field, fieldIndex) => (
                                <Card key={fieldIndex} style={{ marginBottom: 10, padding: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ flex: 1 }}>
                                            <Row className="mb-2">
                                                <Col xs={6}>
                                                    <Form.Label>Field Name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="e.g., Employee Name"
                                                        value={field.name}
                                                        onChange={(e) => updatePersonalizationField(fieldIndex, 'name', e.target.value)}
                                                    />
                                                </Col>
                                                <Col xs={4}>
                                                    <Form.Label>Field Type</Form.Label>
                                                    <Form.Select
                                                        value={field.fieldType}
                                                        onChange={(e) => updatePersonalizationField(fieldIndex, 'fieldType', e.target.value)}
                                                    >
                                                        <option value="text">Text</option>
                                                        <option value="select">Dropdown</option>
                                                    </Form.Select>
                                                </Col>
                                                <Col xs={2}>
                                                    <Form.Label>Required</Form.Label>
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={field.required}
                                                        onChange={(e) => updatePersonalizationField(fieldIndex, 'required', e.target.checked)}
                                                    />
                                                </Col>
                                            </Row>

                                            {field.fieldType === 'text' && (
                                                <Row className="mb-2">
                                                    <Col xs={6}>
                                                        <Form.Label>Placeholder</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Placeholder text"
                                                            value={field.placeholder || ''}
                                                            onChange={(e) => updatePersonalizationField(fieldIndex, 'placeholder', e.target.value)}
                                                        />
                                                    </Col>
                                                    <Col xs={4}>
                                                        <Form.Label>Max Length</Form.Label>
                                                        <Form.Control
                                                            type="number"
                                                            value={field.maxLength || ''}
                                                            onChange={(e) => updatePersonalizationField(fieldIndex, 'maxLength', parseInt(e.target.value) || null)}
                                                        />
                                                    </Col>
                                                </Row>
                                            )}

                                            {field.fieldType === 'select' && (
                                                <div style={{ marginTop: 10 }}>
                                                    <Form.Label>Dropdown Options</Form.Label>
                                                    {(field.options || []).map((option, optionIndex) => (
                                                        <div key={optionIndex} style={{ display: 'flex', marginBottom: 5 }}>
                                                            <Form.Control
                                                                type="text"
                                                                placeholder={`Option ${optionIndex + 1}`}
                                                                value={option}
                                                                onChange={(e) => updateSelectOption(fieldIndex, optionIndex, e.target.value)}
                                                                style={{ marginRight: 5 }}
                                                            />
                                                            <Button variant="outline-danger" size="sm" onClick={() => removeSelectOption(fieldIndex, optionIndex)}>
                                                                <XCircle />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                    <Button variant="outline-secondary" size="sm" onClick={() => addSelectOption(fieldIndex)}>
                                                        + Add Option
                                                    </Button>
                                                </div>
                                            )}
                                            <Row className="mt-2">
                                                <Col xs={4}>
                                                    <Form.Label>Price (optional)</Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                        value={field.price ?? ''}
                                                        onChange={(e) => updatePersonalizationField(fieldIndex, 'price', e.target.value === '' ? '' : e.target.value)}
                                                    />
                                                </Col>
                                            </Row>
                                            <div className="mt-2">
                                                <Form.Label>Decoration Color by Product Color</Form.Label>
                                                {colorsData.filter(c => c.colorId).length === 0 ? (
                                                    <div style={{ color: '#666', fontSize: '0.9em' }}>
                                                        Save the product with colors first, then set decoration colors per product color here (or on Product Details).
                                                    </div>
                                                ) : (
                                                    colorsData.filter(c => c.colorId).map((productColor) => (
                                                        <Row key={productColor.colorId} className="mb-1 align-items-center">
                                                            <Col xs={4} style={{ fontSize: '0.9em' }}>
                                                                {productColor.colorName || `Color #${productColor.colorId}`}
                                                            </Col>
                                                            <Col xs={8}>
                                                                <Form.Select
                                                                    size="sm"
                                                                    value={getMappedDecoColorId(field, productColor.colorId)}
                                                                    onChange={(e) => updateColorMapping(
                                                                        fieldIndex,
                                                                        productColor.colorId,
                                                                        e.target.value === '' ? '' : parseInt(e.target.value, 10)
                                                                    )}
                                                                >
                                                                    <option value="">— None —</option>
                                                                    {decoColors.map((c) => (
                                                                        <option key={c.id} value={c.id}>
                                                                            {c.colorName}{c.thread ? ` (thread ${c.thread})` : ''}{c.pms ? ` · PMS ${c.pms}` : ''}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Col>
                                                        </Row>
                                                    ))
                                                )}
                                            </div>
                                        </div>
                                        <Button variant="outline-danger" size="sm" onClick={() => removePersonalizationField(fieldIndex)} style={{ marginLeft: 10 }}>
                                            <XCircle />
                                        </Button>
                                    </div>
                                </Card>
                            ))}

                            {personalizationFields.length > 0 && productId && (
                                <Button variant="success" size="sm" onClick={savePersonalizationFields} style={{ marginTop: 10 }}>
                                    Save Personalization Fields
                                </Button>
                            )}
                            {personalizationFields.length > 0 && !productId && (
                                <small style={{ color: '#888', display: 'block', marginTop: 10 }}>
                                    Save the product first, then you can save personalization fields.
                                </small>
                            )}
                        </div>

                        <Button onClick={handleSubmit(onSubmit)}>{productId ? 'Edit Product' : 'Add Product'}</Button>
                    </div>
                </div>
            </Form>
        </Container>

    );
}

export default AdminAddProduct;